import {
  weatherCodeEmoji,
  weatherCodeLabel,
} from "@/lib/conditions/weather-code-display";
import { scoreBadgeColor } from "@/lib/conditions/scoring";
import { formatWind, type WindUnit } from "@/lib/conditions/forecast-presentation";
import type { DailyCrowd } from "@/lib/conditions/crowd-prediction";
import { crowdColor, crowdTextColor } from "@/lib/conditions/crowd-prediction";
import { dayLabel, dateLabel } from "@/lib/conditions/forecast-day-format";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";

export type OutlookDay = {
  date: string;
  score: number | null;
  label: string;
  weatherCode: number;
  swellHeightM: number;
  swellPeriodS: number;
  windSpeedKmh: number;
  crowd: DailyCrowd;
  windGustKmh?: number | null;
  waterTempC?: number | null;
  currentVelocityMs?: number | null;
  /** Present only for exact named SUP forecast days. */
  safetyState?: ConditionSafetyState;
};

function safetyQualification(
  state: ConditionSafetyState | undefined,
): { label: string; className: string } | null {
  if (state === "flagged-red") {
    return { label: "Red safety flag", className: "text-red-700" };
  }
  if (state === "flagged-amber") {
    return { label: "Caution", className: "text-amber-700" };
  }
  if (state === "no-data") {
    return {
      label: "Safety evidence unavailable",
      className: "text-text-muted",
    };
  }
  return null;
}

export function WeekOutlook({
  days,
  todayISO,
  bestDate,
  busyPeriodLabel,
  sport = "surf",
  windUnit = "kmh",
}: {
  days: OutlookDay[];
  todayISO: string;
  bestDate: string | null;
  busyPeriodLabel?: string | null;
  sport?: string;
  windUnit?: WindUnit;
}) {
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5 md:gap-3">
        {days.map((d) => {
          const isBest = d.score !== null && d.date === bestDate && d.safetyState !== "flagged-red" && d.safetyState !== "no-data";
          const badge = d.score === null ? { bg: "bg-warm-bg", text: "text-text-muted" } : scoreBadgeColor(d.score);
          const safety = safetyQualification(d.safetyState);
          return (
            <div
              key={d.date}
              className={`rounded-[12px] border p-3.5 text-center transition-colors ${
                isBest
                  ? "bg-gold/5 border-gold/40 ring-1 ring-gold/30"
                  : "bg-white border-border-light"
              }`}
            >
              <div className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted">
                {dayLabel(d.date, todayISO)}
              </div>
              <div className="text-[10px] text-text-muted/80 mb-2">
                {dateLabel(d.date)}
              </div>

              <div
                className="text-[28px] leading-none my-2"
                aria-label={weatherCodeLabel(d.weatherCode)}
                title={weatherCodeLabel(d.weatherCode)}
              >
                {weatherCodeEmoji(d.weatherCode)}
              </div>

              <div
                className={`rounded-[6px] px-1.5 py-0.5 text-center mt-1.5 ${badge.bg} ${badge.text}`}
              >
                <span className="font-serif text-[13px] font-bold tabular-nums">
                  {d.score === null ? "Unscored" : `${scoreOutOf100(d.score)}/100`}
                </span>
              </div>

              <div className="mt-2 space-y-1 text-[11px] leading-relaxed text-text-body">
                {outlookReadings(d, sport, windUnit).map((reading) => <div key={reading}>{reading}</div>)}
              </div>

              <div className="mt-2 flex items-center justify-center gap-1">
                <span
                  className={`w-2 h-2 rounded-full ${crowdColor(d.crowd.level)}`}
                />
                <span
                  className={`text-[10px] font-semibold capitalize ${crowdTextColor(d.crowd.level)}`}
                >
                  {d.crowd.level}
                </span>
              </div>
              {d.crowd.note && (
                <div className="text-[9px] text-text-muted mt-0.5">
                  {d.crowd.note}
                </div>
              )}

              {safety && (
                <div className={`mt-1.5 text-[10px] font-semibold ${safety.className}`}>
                  {safety.label}
                </div>
              )}

              {isBest && (
                <div className="text-[10px] font-semibold text-gold mt-1.5 uppercase tracking-[1px]">
                  Best conditions
                </div>
              )}
            </div>
          );
        })}
      </div>

      {busyPeriodLabel && (
        <div className="col-span-full mt-2.5">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            {busyPeriodLabel}: expect busier conditions this week
          </span>
        </div>
      )}
    </div>
  );
}

export function outlookReadings(day: OutlookDay, sport: string, unit: WindUnit): string[] {
  const wind = `Wind ${formatWind(day.windSpeedKmh, unit)}`;
  const gust = `Gusts ${formatWind(day.windGustKmh, unit)}`;
  const swell = `${day.swellHeightM.toFixed(1)} m swell · ${day.swellPeriodS.toFixed(0)} s`;
  const water = day.waterTempC == null ? "Water unavailable" : `${day.waterTempC.toFixed(1)}°C water`;
  const current = day.currentVelocityMs == null ? "Current unavailable" : `${day.currentVelocityMs.toFixed(2)} m/s current`;
  switch (sport) {
    case "wind": case "wing-foil": return [wind, gust];
    case "diving": return [swell, current, water];
    case "swim": case "swimming": return [water, `${day.swellHeightM.toFixed(1)} m swell`, wind];
    case "sup": case "sup-touring": return [wind, gust, `${day.swellHeightM.toFixed(1)} m swell`];
    case "downwind-foil": case "sup-downwind": return [wind, gust, "Wind-wave outlook unavailable"];
    default: return [swell, wind];
  }
}
